// Penanda jenis wilayah administratif — helper MURNI (tanpa React, tanpa I/O).
//
// Satu-satunya tempat yang memutuskan bagaimana "Garung", "Wonosobo", dan
// "Jawa Tengah" DITULIS di layar. Dipakai baris saran `DistrictPicker` dan dua
// kolom turunan `DistrictDerivedFields`; kalau aturannya disalin ke JSX,
// picker dan kolom di bawahnya akan menulis kabupaten yang sama dengan dua
// bentuk berbeda pada layar yang sama.
//
// ⛔ MODUL INI TIDAK PERNAH MENGUBAH APA YANG DISIMPAN. Yang masuk DB tetap
// `city`/`province` apa adanya dari Biteship; awalan di sini murni lapisan
// tampilan. Menyimpan bentuk berawalan akan membuat `city` tersimpan berbeda
// dari `city_name` di `biteship_areas` — dan itulah persis "dua pembaca tak
// sepakat" yang jadi alasan kolomnya read-only.
//
// ⚠️ KENAPA "Kab./Kota" DAN BUKAN "Kab." — TEMUAN DATA, BUKAN SELERA.
// Biteship TIDAK membedakan kabupaten dan kota pada
// `administrative_division_level_2_name` (= kolom `city_name`). Diperiksa pada
// direktori PROD `biteship_areas` (1.620 baris, 2026-08-20):
//   • 0 baris memuat kata "Kabupaten" / "Kab.";
//   • 4 baris diawali huruf "Kota" dan KEEMPATNYA "Kotawaringin Timur" —
//     kabupaten, bukan kota, dan sekaligus jebakan buat pencocokan awalan
//     yang tak memakai batas kata;
//   • satu nilai "Bandung" (kode kota IDNC22) memuat kecamatan Kota Bandung
//     (Bandung Wetan, Sumur Bandung) DAN Kabupaten Bandung (Pangalengan,
//     Kertasari) sekaligus — jadi bahkan `area_id`-nya pun tak memisahkan.
// Menebak "Kab." dari teksnya sendiri berarti menulis "Kab. Jakarta Pusat",
// "Kab. Surabaya", dan "Kab. Bandung" untuk alamat di Kota Bandung. Karena itu
// nilai yang tak bertanda dibiarkan bertanda GANDA ("Kab./Kota"): hierarkinya
// tetap eksplisit, tapi tak ada fakta yang dikarang.
//
// Kalau suatu hari hulu mulai mengirim "Kabupaten Wonosobo" / "Kota Bandung",
// tak ada yang perlu diubah: cabang `kota`/`kabupaten` di bawah langsung
// mengambil alih dan awalan ganda hilang dengan sendirinya.

/** Jenis wilayah tingkat II, sejauh yang BISA DIKETAHUI dari teksnya. */
export type RegionKind = "kota" | "kabupaten" | "unknown";

export type RegionName = {
  kind: RegionKind;
  /** Nama telanjang — penanda jenis (bila ada) sudah dilepas. */
  name: string;
};

/**
 * Penanda jenis yang DIAKUI pada nama tingkat II.
 *
 * Semua pola diakhiri `\s+` — itulah batas kata yang menyelamatkan
 * "Kotawaringin Timur" (tak ada spasi sesudah "Kota" ⇒ tak cocok ⇒ tetap
 * `unknown`). `^kota\b` TIDAK cukup: `\b` juga tak cocok di situ, tapi pola
 * berbasis `\b` menggoda orang berikutnya menambah varian tanpa spasi.
 *
 * Urutan penting: "Kota Administrasi …" harus diuji SEBELUM "Kota …", kalau
 * tidak sisanya jadi "Administrasi Jakarta Pusat".
 */
const CITY_MARKERS: ReadonlyArray<{ kind: RegionKind; re: RegExp }> = [
  // Penanda GANDA yang dipasang modul ini sendiri, diuji paling awal supaya
  // melabeli hasil label sendiri tak pernah menumpuk jadi "Kab./Kota Kab./Kota
  // Wonosobo". Hari ini tak ada jalur yang mengumpankan keluaran ke masukan,
  // tapi justru itu jenis rantai yang lahir diam-diam saat sebuah nilai
  // tampilan dioper ke fungsi tampilan berikutnya.
  { kind: "unknown", re: /^kab\.?\s*\/\s*kota\s+/i },
  { kind: "kota", re: /^kota\s+adm(?:inistrasi)?\.?\s+/i },
  { kind: "kota", re: /^kotamadya\s+/i },
  { kind: "kota", re: /^kota\.?\s+/i },
  { kind: "kabupaten", re: /^kabupaten\s+/i },
  { kind: "kabupaten", re: /^kab\.?\s+/i },
];

const DISTRICT_MARKER = /^kec(?:amatan)?\.?\s+/i;
const PROVINCE_MARKER = /^prov(?:insi)?\.?\s+/i;

/** Bentuk tampil per jenis. `unknown` sengaja BERTANDA GANDA — lihat kepala file. */
const CITY_PREFIX: Record<RegionKind, string> = {
  kota: "Kota",
  kabupaten: "Kab.",
  unknown: "Kab./Kota",
};

/** Spasi tepi dibuang, spasi ganda dirapatkan, non-string → "". */
function tidy(raw: string | null | undefined): string {
  return typeof raw === "string" ? raw.trim().replace(/\s+/g, " ") : "";
}

/** Lepas satu penanda di depan; kembalikan nilai apa adanya bila tak cocok. */
function stripMarker(value: string, marker: RegExp): string {
  const hit = marker.exec(value);
  if (!hit) return value;
  const rest = value.slice(hit[0].length).trim();
  // Penanda TANPA sisa nama bukan penanda — "Kecamatan" sendirian adalah
  // namanya. Mengembalikan "" di sini akan menghapus satu-satunya teks yang
  // dipunyai baris itu.
  return rest === "" ? value : rest;
}

/**
 * Nama tingkat II → jenis + nama telanjang.
 *
 * `unknown` BUKAN kegagalan: itu jawaban jujur untuk nilai yang memang tak
 * membawa penandanya (mayoritas mutlak data Biteship hari ini).
 */
export function classifyCity(raw: string | null | undefined): RegionName {
  const value = tidy(raw);
  if (value === "") return { kind: "unknown", name: "" };
  for (const marker of CITY_MARKERS) {
    const hit = marker.re.exec(value);
    if (!hit) continue;
    const rest = value.slice(hit[0].length).trim();
    if (rest === "") break;
    return { kind: marker.kind, name: rest };
  }
  return { kind: "unknown", name: value };
}

/**
 * Kabupaten/kota LENGKAP dengan penanda jenis — untuk tempat yang TAK punya
 * label kolom sendiri (baris saran picker). "" bila nilainya kosong.
 */
export function formatCityLabel(raw: string | null | undefined): string {
  const { kind, name } = classifyCity(raw);
  return name === "" ? "" : `${CITY_PREFIX[kind]} ${name}`;
}

/**
 * Kabupaten/kota untuk kolom yang SUDAH berlabel "Kabupaten/Kota".
 *
 * Bedanya dengan `formatCityLabel` cuma pada jenis `unknown`: di bawah label
 * yang sudah berbunyi "Kabupaten/Kota", menulis "Kab./Kota Wonosobo" adalah
 * label yang sama dua kali. Yang SUDAH diketahui jenisnya tetap ditandai —
 * "Kota Bandung" di bawah label itu menambah informasi, bukan mengulanginya.
 */
export function formatCityFieldValue(raw: string | null | undefined): string {
  const { kind, name } = classifyCity(raw);
  if (name === "") return "";
  return kind === "unknown" ? name : `${CITY_PREFIX[kind]} ${name}`;
}

/**
 * Kecamatan → "Kec. <nama>". Aman ditulis tanpa menebak apa pun: kolomnya
 * memang tingkat III (`administrative_division_level_3_name`). Nilai yang
 * sudah membawa "Kecamatan "/"Kec. " dinormalkan, tidak ditumpuk.
 */
export function formatDistrictLabel(raw: string | null | undefined): string {
  const value = tidy(raw);
  if (value === "") return "";
  return `Kec. ${stripMarker(value, DISTRICT_MARKER)}`;
}

/**
 * Provinsi → nama telanjang ("Provinsi Jawa Tengah" → "Jawa Tengah").
 *
 * Tanpa awalan: di baris saran posisinya sudah menjelaskan tingkatannya, dan
 * di kolom turunan labelnya sudah berbunyi "Provinsi".
 */
export function formatProvinceLabel(raw: string | null | undefined): string {
  const value = tidy(raw);
  if (value === "") return "";
  return stripMarker(value, PROVINCE_MARKER);
}

/**
 * Baris kedua satu saran kecamatan: "Kab. Wonosobo · Jawa Tengah · 56353".
 *
 * Bagian yang kosong DIBUANG, bukan disisakan sebagai pemisah menggantung —
 * area tanpa kode pos akan berakhir "… · Jawa Tengah ·" kalau tidak.
 */
export function formatAreaSecondaryLine(area: {
  city?: string | null;
  province?: string | null;
  postalCode?: string | null;
}): string {
  return [
    formatCityLabel(area.city),
    formatProvinceLabel(area.province),
    tidy(area.postalCode),
  ]
    .filter((part) => part !== "")
    .join(" · ");
}
